import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Star } from 'lucide-react';
export function PricingSection() {
  const INITIAL_SECONDS = 30 * 60; // 30 minutes
  const [timeLeft, setTimeLeft] = useState(INITIAL_SECONDS);
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (timeLeft <= 0) {
      setExpired(true);
      return;
    }
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const isUrgent = timeLeft <= 5 * 60; // last 5 minutes
  return <section id="comprar-ahora" className="py-20 bg-gradient-to-br from-violet-700 via-violet-600 to-purple-700 relative overflow-hidden" data-id="element-233">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10" data-id="element-234">
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-white blur-3xl" data-id="element-235" />
        <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-orange-300 blur-3xl" data-id="element-236" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-3xl" data-id="element-237">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-238">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight" data-id="element-239">
            🎁 <span className="text-orange-400" data-id="element-240">+60% OFF</span> OFERTA ESPECIAL
            POR TIEMPO LIMITADO
          </h2>
        </motion.div>

        {/* Bundles */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-241">
          {/* Item 1 */}
          <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl p-5 text-center flex-1 w-full sm:w-auto" data-id="element-242">
            <p className="text-white font-extrabold text-sm uppercase tracking-wide mb-1" data-id="element-243">
              ✨ RECETARIO COMPLETO
            </p>
            <p className="text-orange-300 line-through text-base font-bold" data-id="element-244">
              $34.900
            </p>
          </div>

          <div className="text-white text-4xl font-black" data-id="element-245">+</div>

          {/* Item 2 */}
          <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl p-5 text-center flex-1 w-full sm:w-auto" data-id="element-246">
            <p className="text-white font-extrabold text-sm uppercase tracking-wide mb-1" data-id="element-247">
              🛒 BONUS 1: Lista de Compras "Hacker"
            </p>
            <p className="text-orange-300 line-through text-base font-bold" data-id="element-248">
              $8.000
            </p>
          </div>

          <div className="text-white text-4xl font-black" data-id="element-249">+</div>

          {/* Item 3 */}
          <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl p-5 text-center flex-1 w-full sm:w-auto" data-id="element-250">
            <p className="text-white font-extrabold text-sm uppercase tracking-wide mb-1" data-id="element-251">
              📋 BONUS 2: Menú Semanal Imprimible
            </p>
            <p className="text-orange-300 line-through text-base font-bold" data-id="element-252">
              $7.000
            </p>
          </div>
        </motion.div>

        {/* Price Box */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="bg-white rounded-2xl p-6 text-center mb-6 shadow-2xl" data-id="element-253">
          <p className="text-gray-700 font-extrabold text-xl md:text-2xl mb-1" data-id="element-254">
            TODO POR SOLO{' '}
            <span className="text-emerald-600 text-3xl md:text-4xl underline decoration-2" data-id="element-255">
              $19.900
            </span>
          </p>
          <p className="text-orange-500 font-extrabold text-lg" data-id="element-256">
            ¡Ahorrás $30.000! (60% OFF)
          </p>
        </motion.div>

        {/* Countdown + CTA Box */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-2xl p-8 text-center" data-id="element-257">
          {/* Urgency label */}
          <p className="text-white/80 font-bold text-sm uppercase tracking-widest mb-4" data-id="element-258">
            APROVECHÁ AHORA Y LLEVALO CON{' '}
            <span className="text-orange-400" data-id="element-259">+60% OFF</span>
          </p>

          {/* Countdown */}
          {!expired ? <div className="flex justify-center gap-6 mb-4" data-id="element-260">
              {[{
            value: pad(hours),
            label: 'HORAS'
          }, {
            value: pad(minutes),
            label: 'MINUTOS'
          }, {
            value: pad(seconds),
            label: 'SEGUNDOS'
          }].map(({
            value,
            label
          }) => <div key={label} className="flex flex-col items-center" data-id="element-261">
                  <motion.span key={value} initial={{
              scale: 1.2,
              opacity: 0.7
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.2
            }} className={`text-5xl md:text-6xl font-black tabular-nums ${isUrgent ? 'text-red-400' : 'text-white'}`} data-id="element-262">
                    {value}
                  </motion.span>
                  <span className="text-white/60 text-xs font-bold tracking-widest mt-1" data-id="element-263">
                    {label}
                  </span>
                </div>)}
            </div> : <p className="text-red-400 font-extrabold text-xl mb-4" data-id="element-264">
              ⏰ ¡La oferta expiró!
            </p>}

          {/* Scarcity */}
          <p className="text-orange-300 font-bold text-sm mb-6" data-id="element-265">
            🔥 Solo quedan 5 copias con descuento
          </p>

          {/* CTA Button */}
          <motion.button onClick={() => window.open('https://cocinasimpleya.com/cart/53934251802908:1', '_blank')} whileHover={{
          scale: 1.08,
          boxShadow: '0 0 60px rgba(251,191,36,0.95), 0 0 35px rgba(251,146,60,0.85)'
        }} whileTap={{
          scale: 0.96
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 18
        }} className="relative bg-gradient-to-r from-yellow-400 via-orange-400 to-orange-500 text-gray-900 font-extrabold text-2xl md:text-3xl px-14 py-6 rounded-full shadow-2xl cursor-pointer w-full sm:w-auto border-4 border-yellow-200 overflow-hidden group" data-id="element-266">
            <motion.span className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-orange-300 to-yellow-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-267" />
            <span className="relative z-10 flex items-center justify-center gap-3" data-id="element-268">
              🛒 ¡COMPRAR AHORA!
            </span>
          </motion.button>

          {/* Stars */}
          <div className="flex items-center justify-center gap-2 mt-6" data-id="element-269">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" data-id="element-270" />)}
            <span className="text-white/80 font-semibold text-sm ml-1" data-id="element-271">
              4.9/5 opiniones
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}